/**
 * @fileoverview In-session journey dimensions for analytics.
 * @description Tracks page depth and intent progression within a browser tab
 * (sessionStorage). Emitted on `page_view` and `page_engagement` so GA4
 * Explorations can answer "how many pages before conversion?" and "did
 * evaluation pages precede the lead?" without session replay.
 *
 * Consent-gated writes mirror attribution: sessionStorage updates only when
 * {@link isAnalyticsStorageAllowed} is true (region-aware explicit opt-in).
 *
 * @module lib/site-intelligence/session-journey
 */

import {
  CONSENT_CHANGED_EVENT,
  CONSENT_STORAGE_KEY,
  isAnalyticsStorageAllowed,
  hasExplicitAnalyticsOptOut,
} from "~/lib/analytics/consent"
import { sectionForPath } from "~/lib/analytics/attribution"

import { intentForPath, maxIntent, type VisitorIntent } from "./intent"

/** sessionStorage key for the in-tab journey snapshot. */
export const SESSION_JOURNEY_STORAGE_KEY = "adsights_session_journey_v1"

/** Max distinct sections tracked (cardinality guard for GA4). */
const MAX_SECTIONS = 8

let optOutPurgeListenersBound = false

/** Unique per document load — scopes same-path dedupe to mount-order races, not reloads. */
let documentInstanceId: string | null = null

function getDocumentInstanceId(): string {
  documentInstanceId ??= `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
  return documentInstanceId
}

/**
 * Resets the document instance id (test-only — simulates a hard reload).
 *
 * @returns {void}
 * @internal
 */
export function resetJourneyDocumentInstanceForTests(): void {
  documentInstanceId = null
}

function bindOptOutPurgeListeners(): void {
  if (optOutPurgeListenersBound || typeof window === "undefined") return
  optOutPurgeListenersBound = true

  const purgeIfOptedOut = (): void => {
    if (hasExplicitAnalyticsOptOut()) {
      resetSessionJourney()
    }
  }

  window.addEventListener(CONSENT_CHANGED_EVENT, purgeIfOptedOut)
  window.addEventListener("storage", (event: StorageEvent) => {
    if (event.key === null || event.key === CONSENT_STORAGE_KEY) {
      purgeIfOptedOut()
    }
  })
}

/** Low-cardinality props attached to page-level analytics events. */
export type JourneyPageProps = Readonly<{
  site_section: string
  page_intent: VisitorIntent
  session_page_index: number
  session_max_intent: VisitorIntent
  session_section_count: number
}>

interface StoredJourney {
  page_index: number
  sections: string[]
  max_intent: VisitorIntent
  /** Last pathname recorded — idempotent re-entry for mount-order races. */
  current_path?: string
  /** Document instance that last incremented page_index. */
  last_document_id?: string
}

function emptyJourney(): StoredJourney {
  return { page_index: 0, sections: [], max_intent: "awareness" }
}

function readStored(): StoredJourney {
  try {
    const raw = sessionStorage.getItem(SESSION_JOURNEY_STORAGE_KEY)
    if (!raw) return emptyJourney()
    const parsed = JSON.parse(raw) as Partial<StoredJourney>
    const page_index =
      typeof parsed.page_index === "number" && Number.isFinite(parsed.page_index)
        ? Math.max(0, Math.floor(parsed.page_index))
        : 0
    const sections = Array.isArray(parsed.sections)
      ? parsed.sections.filter((s): s is string => typeof s === "string").slice(0, MAX_SECTIONS)
      : []
    const max_intent =
      parsed.max_intent === "exploration" ||
      parsed.max_intent === "evaluation" ||
      parsed.max_intent === "conversion"
        ? parsed.max_intent
        : "awareness"
    const current_path =
      typeof parsed.current_path === "string" ? parsed.current_path : undefined
    const last_document_id =
      typeof parsed.last_document_id === "string" ? parsed.last_document_id : undefined
    return { page_index, sections, max_intent, current_path, last_document_id }
  } catch {
    return emptyJourney()
  }
}

function writeStored(value: StoredJourney): void {
  try {
    sessionStorage.setItem(SESSION_JOURNEY_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Quota/unavailable — analytics must never break UX.
  }
}

function journeyPropsFromStored(
  stored: StoredJourney,
  path: string,
): JourneyPageProps {
  const site_section = sectionForPath(path)
  const page_intent = intentForPath(path)
  return {
    site_section,
    page_intent,
    session_page_index: stored.page_index,
    session_max_intent: stored.max_intent,
    session_section_count: stored.sections.length,
  }
}

/** Safe defaults when analytics storage is not readable/writable. */
function defaultJourneyProps(path: string): JourneyPageProps {
  return {
    site_section: sectionForPath(path),
    page_intent: intentForPath(path),
    session_page_index: 1,
    session_max_intent: intentForPath(path),
    session_section_count: 1,
  }
}

/**
 * Records a page view in the session journey and returns dimensions for analytics.
 *
 * Increments `session_page_index`, appends the page's site section (deduped,
 * capped), and advances `session_max_intent` when the visitor reaches a deeper
 * funnel stage. Call once per SPA navigation before emitting `page_view`.
 *
 * @param {string} path - Current pathname (query string excluded).
 * @returns {JourneyPageProps} Dimensions to merge into `page_view` / engagement events.
 */
export function recordJourneyPage(path: string): JourneyPageProps {
  bindOptOutPurgeListeners()
  if (hasExplicitAnalyticsOptOut()) {
    resetSessionJourney()
    return defaultJourneyProps(path)
  }
  if (!isAnalyticsStorageAllowed()) {
    return defaultJourneyProps(path)
  }

  const stored = readStored()
  const site_section = sectionForPath(path)
  const page_intent = intentForPath(path)

  // Idempotent within the same document (conversion beacon before layout pageview).
  if (
    stored.current_path === path &&
    stored.last_document_id === getDocumentInstanceId() &&
    stored.page_index > 0
  ) {
    return journeyPropsFromStored(stored, path)
  }

  const nextIndex = stored.page_index + 1
  const sections = stored.sections.includes(site_section)
    ? stored.sections
    : [...stored.sections, site_section].slice(0, MAX_SECTIONS)
  const nextMaxIntent = maxIntent(stored.max_intent, page_intent)

  writeStored({
    page_index: nextIndex,
    sections,
    max_intent: nextMaxIntent,
    current_path: path,
    last_document_id: getDocumentInstanceId(),
  })

  return {
    site_section,
    page_intent,
    session_page_index: nextIndex,
    session_max_intent: nextMaxIntent,
    session_section_count: sections.length,
  }
}

/**
 * Returns the current journey snapshot without incrementing page index.
 *
 * @returns {JourneyPageProps | null} Latest journey props, or null if unavailable.
 */
export function peekJourneyProps(path: string): JourneyPageProps | null {
  bindOptOutPurgeListeners()
  if (hasExplicitAnalyticsOptOut()) {
    resetSessionJourney()
    return null
  }
  if (!isAnalyticsStorageAllowed()) return null
  const stored = readStored()
  if (stored.page_index <= 0) return null
  return journeyPropsFromStored(stored, path)
}

/**
 * Clears session journey state (testing / consent opt-out recovery).
 *
 * @returns {void}
 */
export function resetSessionJourney(): void {
  try {
    sessionStorage.removeItem(SESSION_JOURNEY_STORAGE_KEY)
  } catch {
    // ignore
  }
}
